/**
 * `next/image` — a plain <img>.
 *
 * The optimizer it wraps was a server feature; without a Next server there is nothing to
 * optimize to, so the honest shim is the element itself. `width`/`height` pass straight
 * through as attributes, which is what keeps the three shell call sites (the SIYA logo in
 * the header, and two icons) laid out exactly as before.
 *
 * `priority`, `quality`, `placeholder`, `loader` and `fill` are accepted and dropped —
 * they configure an optimizer that no longer exists. `fill` is the one with a visual
 * consequence, so it is translated to the absolute-fill style Next applies for it rather
 * than silently ignored.
 */

import React from 'react';

interface NextImageProps extends Omit<React.ImgHTMLAttributes<HTMLImageElement>, 'src'> {
  src: string | { src: string };
  width?: number | string;
  height?: number | string;
  fill?: boolean;
  priority?: boolean;
  quality?: number;
  placeholder?: string;
  unoptimized?: boolean;
}

export default function NextImage({
  src,
  fill,
  priority,
  quality: _quality,
  placeholder: _placeholder,
  unoptimized: _unoptimized,
  style,
  alt = '',
  ...rest
}: NextImageProps) {
  const resolvedSrc = typeof src === 'string' ? src : src.src;

  return (
    <img
      src={resolvedSrc}
      alt={alt}
      // Next sets fetchPriority="high" for `priority`; it is the one part of that prop
      // that still applies without the optimizer.
      fetchPriority={priority ? 'high' : undefined}
      style={
        fill
          ? { position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', ...style }
          : style
      }
      {...rest}
    />
  );
}
